import { FC, useEffect, useState } from 'react';
import { Button, Dialog, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { TEAM_BETA_FEATURES } from '../constants';
import { ReactComponent as TeamFilledIcon } from '../../../assets/sideNavigation/team_people_filled.svg';
import './TeamBetaIntroModal.scss';

const TEAM_PLAN_INTERVIEW_URL = 'https://calendly.com/hayatowaki/lifestack-onboarding-w-hayato';

interface TeamBetaIntroModalProps {
  open: boolean;
  onContinue: () => void;
  showScheduleLink?: boolean;
}

const TeamBetaIntroModal: FC<TeamBetaIntroModalProps> = ({
  open,
  onContinue,
  showScheduleLink = false,
}) => {
  const { t } = useTranslation();
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const activeFeature = TEAM_BETA_FEATURES[activeFeatureIndex];
  const isLastFeature = activeFeatureIndex === TEAM_BETA_FEATURES.length - 1;
  const getFeatureCopy = (field: 'navTitle' | 'title' | 'description') =>
    t(`team.beta.features.${activeFeature.i18nKey}.${field}`);

  useEffect(() => {
    if (open) {
      setActiveFeatureIndex(0);
    }
  }, [open]);

  const handleNext = () => {
    if (isLastFeature) {
      onContinue();
      return;
    }

    setActiveFeatureIndex((currentIndex) => currentIndex + 1);
  };

  const handleBack = () => {
    setActiveFeatureIndex((currentIndex) => Math.max(currentIndex - 1, 0));
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      className="team-beta-intro-modal"
      onClose={onContinue}
      aria-labelledby="team-beta-intro-modal-title"
    >
      <div className="team-beta-intro-modal__content">
        <div
          className="team-beta-intro-modal__tour-rail"
          aria-label={t('team.beta.ariaWalkthrough')}
        >
          <div className="team-beta-intro-modal__beta-pill">
            <TeamFilledIcon className="team-beta-intro-modal__beta-icon" />
            <span>{t('team.common.team')}</span>
            <strong>{t('team.common.beta')}</strong>
          </div>

          <div className="team-beta-intro-modal__steps">
            {TEAM_BETA_FEATURES.map((feature, index) => (
              <button
                key={feature.i18nKey}
                type="button"
                className={`team-beta-intro-modal__step ${
                  index === activeFeatureIndex ? 'team-beta-intro-modal__step--active' : ''
                }`}
                onClick={() => setActiveFeatureIndex(index)}
                aria-current={index === activeFeatureIndex ? 'step' : undefined}
              >
                <span className="team-beta-intro-modal__step-index">{index + 1}</span>
                <span>{t(`team.beta.features.${feature.i18nKey}.navTitle`)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="team-beta-intro-modal__video-card">
          <div className="team-beta-intro-modal__video-chrome">
            <span />
            <span />
            <span />
            <p>{getFeatureCopy('navTitle')}</p>
          </div>
          <div className="team-beta-intro-modal__video-shell">
            <video
              key={activeFeature.videoSrc}
              className="team-beta-intro-modal__video"
              src={activeFeature.videoSrc}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="team-beta-intro-modal__copy" key={activeFeature.i18nKey}>
          <Typography
            id="team-beta-intro-modal-title"
            variant="h2"
            className="team-beta-intro-modal__title"
          >
            {getFeatureCopy('title')}
          </Typography>
          <Typography variant="body2" className="team-beta-intro-modal__subtitle">
            {getFeatureCopy('description')}
          </Typography>
          {isLastFeature && showScheduleLink && (
            <Typography variant="body2" className="team-beta-intro-modal__access-note">
              {t('team.beta.accessNote')}
            </Typography>
          )}
        </div>

        <div className="team-beta-intro-modal__footer">
          <div>
            <Typography variant="body2" className="team-beta-intro-modal__feedback-text">
              {t('team.beta.contactPrefix')}{' '}
              <a href="mailto:contact@demind-inc.com">contact@demind-inc.com</a>
            </Typography>
          </div>

          <div className="team-beta-intro-modal__footer-actions">
            {activeFeatureIndex > 0 && (
              <Button
                type="button"
                variant="text"
                className="team-beta-intro-modal__back-button"
                onClick={handleBack}
              >
                {t('team.common.back')}
              </Button>
            )}
            {isLastFeature && showScheduleLink ? (
              <>
                <Button
                  type="button"
                  variant="text"
                  className="team-beta-intro-modal__back-button"
                  onClick={onContinue}
                >
                  {t('team.beta.maybeLater')}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  className="team-beta-intro-modal__continue-button"
                  onClick={() =>
                    window.open(TEAM_PLAN_INTERVIEW_URL, '_blank', 'noopener,noreferrer')
                  }
                >
                  {t('team.beta.scheduleCall')}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="contained"
                className="team-beta-intro-modal__continue-button"
                onClick={handleNext}
              >
                {isLastFeature ? t('team.common.continue') : t('team.common.next')}
                {!isLastFeature && (
                  <span className="team-beta-intro-modal__continue-arrow" aria-hidden="true">
                    →
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TeamBetaIntroModal;
